import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  RotateCcw, 
  Share2, 
  Download, 
  Shield,
  Eye,
  EyeOff
} from 'lucide-react';
import { API_URL } from '../config';
import ConsultList from './ConsultList';
import { generateReport } from '../utils/reportGenerator';
import NoseSimulation from './NoseSimulation';

const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
`;

const HeroSection = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  padding: 2rem 2.5rem;
  background: linear-gradient(135deg, rgba(243, 232, 255, 0.8) 0%, rgba(237, 233, 254, 0.6) 100%);
  border: 2px solid rgba(233, 213, 255, 0.5);
  border-radius: 20px;
  color: #1a1a1a;
  box-shadow: 0 4px 6px -1px rgba(139, 92, 246, 0.1);
  animation: fadeInUp 0.6s ease-out;
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const HeroTitle = styled.h1`
  font-size: 2.25rem;
  font-weight: 800;
  margin-bottom: 0.75rem;
  color: #0f172a;
  letter-spacing: -0.02em;
  
  @media (max-width: 768px) {
    font-size: 1.875rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.0625rem;
  color: #64748b;
  font-weight: 400;
  line-height: 1.6;
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ImageSection = styled.div`
  background: white;
  border-radius: 20px;
  padding: 1.5rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }
`;

const ImageContainer = styled.div`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 1.25rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s ease;
  
  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: auto;
`;

const ResultImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 12px;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.02);
  }
`;

const HeatmapImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  opacity: ${props => props.visible ? 0.35 : 0};
  transition: opacity 0.3s ease;
  pointer-events: none;
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid ${props => props.active ? '#3a5cf7' : '#d1d5db'};
  background: ${props => props.active ? '#3a5cf7' : 'white'};
  color: ${props => props.active ? 'white' : '#374151'};
  width: 100%;
  justify-content: center;
  
  &:hover {
    background: ${props => props.active ? '#2d4ae8' : '#f3f4f6'};
    border-color: ${props => props.active ? '#2d4ae8' : '#9ca3af'};
  }
`;

const ConsultSection = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.625rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  letter-spacing: -0.01em;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 1.5rem;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  
  ${props => props.variant === 'primary' && `
    background: linear-gradient(135deg, #3a5cf7 0%, #2d4ae8 100%);
    color: white;
    box-shadow: 0 4px 6px -1px rgba(58, 92, 247, 0.3);
    
    &:hover {
      background: linear-gradient(135deg, #2d4ae8 0%, #1e3a8a 100%);
      transform: translateY(-3px);
      box-shadow: 0 12px 24px -4px rgba(58, 92, 247, 0.4);
    }
  `}
  
  ${props => props.variant === 'secondary' && `
    background: white;
    color: #475569;
    border: 2px solid #e2e8f0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    
    &:hover {
      background: #f8fafc;
      border-color: #cbd5e1;
      transform: translateY(-2px);
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
  `}
  
  ${props => props.variant === 'success' && `
    background: #10b981;
    color: white;
    
    &:hover {
      background: #059669;
      transform: translateY(-2px);
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

function AnalysisResults({ data, onReset }) {
  const [isSharing, setIsSharing] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(false);

  const getFullUrl = (url) => {
    if (!url) return url;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // For image URLs, use backend URL if available
    const isDev = process.env.NODE_ENV === 'development';
    const baseUrl = API_URL || (isDev ? '' : 'https://twodmvp.onrender.com');
    return baseUrl + url;
  };

  const handleShare = async () => {
    setIsSharing(true);
    try {
      // Track share event
      const { trackShare } = require('../utils/analytics');
      
      if (navigator.share) {
        await navigator.share({
          title: 'My Rhinovate AI Consultation',
          text: `Check out my AI-powered cosmetic consultation results!`,
          url: window.location.href,
        });
        trackShare('native_share');
      } else {
        await navigator.clipboard.writeText(
          `Check out my AI-powered cosmetic consultation results at ${window.location.href}`
        );
        alert('Results copied to clipboard!');
        trackShare('clipboard');
      }
    } catch (err) {
      console.error('Error sharing:', err);
    } finally {
      setIsSharing(false);
    }
  };

  const handleDownloadReport = async () => {
    setIsGeneratingReport(true);
    try {
      // Track download event
      const { trackShare } = require('../utils/analytics');
      
      const beforeUrl = data.before_url ? getFullUrl(data.before_url) : null;
      const heatmapUrl = data.visuals?.heatmap || null;
      
      await generateReport(data, beforeUrl, heatmapUrl);
      trackShare('download_report');
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report. Please try again.');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const consultation = data?.measurements?.three_d_metrics?.consultation;
  const cards = consultation?.cards || [];
  const disclaimer = consultation?.disclaimer;

  return (
    <ResultsContainer>
      <HeroSection>
        <HeroTitle>Your AI Consultation</HeroTitle>
        <HeroSubtitle>Educational only; not medical advice</HeroSubtitle>
      </HeroSection>

      <MainContent>
        <LeftColumn>
          {data.before_url && (
            <ImageSection>
              <ImageContainer>
                <ImageWrapper>
                  <ResultImage 
                    src={getFullUrl(data.before_url)} 
                    alt="Face analysis" 
                  />
                  {data.visuals?.heatmap && (
                    <HeatmapImage
                      src={data.visuals.heatmap}
                      alt="Depth heatmap"
                      visible={showHeatmap}
                    />
                  )}
                </ImageWrapper>
              </ImageContainer>
              {data.visuals?.heatmap && (
                <ToggleButton
                  active={showHeatmap}
                  onClick={() => setShowHeatmap(!showHeatmap)}
                >
                  {showHeatmap ? <EyeOff size={20} /> : <Eye size={20} />}
                  {showHeatmap ? 'Hide Depth Map' : 'Show Depth Map'}
                </ToggleButton>
              )}
            </ImageSection>
          )}
        </LeftColumn>

        <RightColumn>
          <ConsultSection>
            <SectionTitle>
              <Shield size={24} />
              Consultation Findings
            </SectionTitle>
            <ConsultList cards={cards} disclaimer={disclaimer} />
          </ConsultSection>
        </RightColumn>
      </MainContent>

      {data.before_url && (
        <NoseSimulation 
          imageUrl={data.before_url}
          onImageGenerated={(afterUrl) => {
            // Optional: Update state or trigger any side effects
            console.log('Generated after image:', afterUrl);
          }}
        />
      )}

      <ActionButtons>
        <ActionButton 
          variant="primary" 
          onClick={handleDownloadReport}
          disabled={isGeneratingReport}
        >
          <Download size={20} />
          {isGeneratingReport ? 'Generating...' : 'Download Report'}
        </ActionButton>
        
        <ActionButton variant="primary" onClick={handleShare} disabled={isSharing}>
          <Share2 size={20} />
          {isSharing ? 'Sharing...' : 'Share'}
        </ActionButton>
        
        <ActionButton variant="secondary" onClick={onReset}>
          <RotateCcw size={20} />
          Try Another Photo
        </ActionButton>
      </ActionButtons>
    </ResultsContainer>
  );
}

export default AnalysisResults;
